import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFirebase } from 'contexts/FirebaseContext';

export default function AuthInitializer({ children }) {
    const [isInitialized, setIsInitialized] = useState(false);
    const { token, signedIn } = useSelector((state) => state.auth.session);
    const user = useSelector((state) => state.auth.user);
    const { initializeForDashboardUser, isInitialized: firebaseInitialized, updateUserOnlineStatus } = useFirebase();
    const firebaseInitializedRef = useRef(false);

    const initializeAuth = useCallback(async () => {
        try {
            // If no token or not signed in, mark as initialized immediately
            if (!token || !signedIn) {
                setIsInitialized(true);
                return;
            }

            // If user exists and Firebase is not yet initialized for this user, set up Firebase auth (only once)
            if (user && !firebaseInitializedRef.current) {
                try {
                    // Initialize Firebase for this dashboard user
                    const firebaseUid = await initializeForDashboardUser(
                        user._id,
                        user.name || user.username,
                        user.userType || 'admin',
                        {
                            name: user.name || user.username,
                            email: user.email,
                            avatar: user.avatar || user.profile?.avatar || '',
                            role: user.userType || 'admin'
                        }
                    );

                    if (firebaseUid) {
                        firebaseInitializedRef.current = true;

                        // Update user online status in Firebase
                        try {
                            await updateUserOnlineStatus(user._id, true, user.userType || 'admin');
                        } catch (statusError) {
                            console.log("⚠️ [AUTH_INITIALIZER] Failed to update user online status:", statusError);
                        }
                    }
                } catch (firebaseError) {
                    console.log("⚠️ [AUTH_INITIALIZER] Firebase initialization failed:", firebaseError);
                }
            }
        } catch (error) {
            console.log('❌ [AUTH_INITIALIZER] Error initializing auth:', error);
        } finally {
            // Mark initialization as complete regardless of outcome
            setIsInitialized(true);
        }
    }, [token, signedIn, user, initializeForDashboardUser, updateUserOnlineStatus]);

    useEffect(() => {
        // Use a timeout to prevent blocking the UI for too long
        const timeoutId = setTimeout(initializeAuth, 100);

        return () => clearTimeout(timeoutId);
    }, [initializeAuth]);

    // Always render children - let the main dashboard handle loading states
    return children;
}
